/**
 * EXTRATO SICREDI PELA API (30/10/2026): a conta #1 é atualizada pelo cron do backend. O card mostra quando foi a última
 * leitura (`balanceCheckedAt`), quando será a próxima, e acusa quando a última ficou para trás.
 *
 * ⭐ Desde 31/10/2026 (backend #585) QUEM DIZ O HORÁRIO É O SERVIDOR: o `GET /bank-accounts` devolve, na conta do
 * extrato, `proximaLeituraEm`, `ultimoDisparoEsperadoEm`, `tipoDeHoje` e `horasDeHoje`, calculados pela MESMA regra que
 * dispara o cron. A tela não repete a regra: a cópia que morava aqui não conhecia o horário de fim de semana, e dizia
 * "seg 07:00" no sábado em que a próxima era 14h do mesmo dia. Aqui só se formata.
 *
 * ⚠️ FERIADO: não há fonte de feriados no HUB (decisão de 30/10/2026 de seguir sem uma). Em feriado o servidor diz dia
 * útil, e a tela NÃO escreve a regra do fim de semana: ela descreve só as horas que `horasDeHoje` traz para hoje.
 * ⚠️ Os quatro campos vêm nulos nas contas que a integração não lê (inclusive a outra conta Sicredi): sem agenda, o card
 * não fala de próxima leitura.
 */

/** Folga para o atraso: o ciclo leva alguns segundos a minutos (consulta ao banco + gravação). */
export const FOLGA_DO_CICLO_MS = 15 * 60 * 1000;
const HORA_MS = 60 * 60 * 1000;
const DIA_MS = 24 * HORA_MS;
const DESLOCAMENTO_BRT_MS = 3 * HORA_MS;
const DIAS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

export const FONTE_API = 'api';

export function contaPelaApi(acc: { balanceSource?: string | null } | null | undefined): boolean {
  return acc?.balanceSource === FONTE_API;
}

/** A agenda que o servidor manda na conta do extrato. Nula fora dela. */
export interface AgendaDoExtrato {
  proximaLeituraEm?: string | null;
  ultimoDisparoEsperadoEm?: string | null;
  tipoDeHoje?: 'util' | 'sem_movimento' | null;
  horasDeHoje?: readonly number[] | null;
}

/** Relógio de parede de Brasília representado nos campos UTC de um Date (só para conta interna). */
const paraBrt = (instante: Date) => new Date(instante.getTime() - DESLOCAMENTO_BRT_MS);
const instanteDe = (v: string | Date | null | undefined): number | null => {
  if (!v) return null;
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? null : t;
};

/**
 * A leitura ficou para trás: o cron já devia ter rodado depois dela (passada a folga do ciclo). Sem leitura nenhuma,
 * também está atrasada. É o que impede "Próxima: 11:00" de parecer saudável ao lado de uma "Última" de ontem.
 *
 * ⚠️ Nos primeiros minutos depois de um disparo, o ciclo ainda pode estar gravando: aí a régua é o disparo ANTERIOR do
 * mesmo dia, achado em `horasDeHoje`. Se o disparo esperado foi o primeiro do dia, não há anterior conhecido e a tela não
 * acusa: o servidor não mandou a véspera, e inventá-la seria repetir a regra que saiu daqui.
 */
export function leituraAtrasada(checadoEm: string | Date | null | undefined, agenda: AgendaDoExtrato, agora: Date): boolean {
  const lida = instanteDe(checadoEm);
  if (lida === null) return true;
  const esperado = instanteDe(agenda.ultimoDisparoEsperadoEm);
  if (esperado === null) return false;
  if (agora.getTime() - esperado >= FOLGA_DO_CICLO_MS) return lida < esperado - FOLGA_DO_CICLO_MS;
  const horaEsperada = paraBrt(new Date(esperado)).getUTCHours();
  const anteriores = (agenda.horasDeHoje || []).filter((h) => h < horaEsperada);
  if (!anteriores.length) return false;
  const anterior = esperado - (horaEsperada - Math.max(...anteriores)) * HORA_MS;
  return lida < anterior - FOLGA_DO_CICLO_MS;
}

/** A agenda envelheceu: o disparo que ela chamava de "próximo" já passou (com a folga), e a tela precisa reler a conta. */
export function agendaVencida(agenda: AgendaDoExtrato, agora: Date): boolean {
  const proxima = instanteDe(agenda.proximaLeituraEm);
  return proxima !== null && agora.getTime() >= proxima + FOLGA_DO_CICLO_MS;
}

/** "hoje 11:00", "amanhã 07:00", "seg 07:00" — o dia relativo ao de `agora`, em Brasília. */
export function rotuloDaProxima(proxima: Date, agora: Date): string {
  const p = paraBrt(proxima);
  const a = paraBrt(agora);
  const hora = `${String(p.getUTCHours()).padStart(2, '0')}:00`;
  const diaP = Math.floor(p.getTime() / DIA_MS);
  const diaA = Math.floor(a.getTime() / DIA_MS);
  if (diaP === diaA) return `hoje ${hora}`;
  if (diaP === diaA + 1) return `amanhã ${hora}`;
  return `${DIAS[p.getUTCDay()]} ${hora}`;
}

/** A linha "Próxima: …" do card. Sem a agenda (backend antigo, ou conta fora do extrato), um traço. */
export function textoDaProxima(agenda: AgendaDoExtrato, agora: Date): string {
  const proxima = instanteDe(agenda.proximaLeituraEm);
  return proxima === null ? 'Próxima: —' : `Próxima: ${rotuloDaProxima(new Date(proxima), agora)}`;
}

/**
 * As leituras de hoje, pelo que o servidor mandou em `horasDeHoje`: horas seguidas viram "de hora em hora, das 7h às
 * 20h"; espaçadas, a lista ("8h, 11h, 14h, 17h e 20h"). ⛔ Não lê o `tipoDeHoje` para isto: deduzir as horas dele seria
 * a segunda cópia da regra. Sem horas, nada a dizer.
 */
export function textoDasLeiturasDeHoje(agenda: AgendaDoExtrato): string | null {
  const h = [...(agenda.horasDeHoje || [])].filter((x) => Number.isFinite(x)).sort((a, b) => a - b);
  if (!h.length) return null;
  const seguidas = h.length > 2 && h.every((x, i) => i === 0 || x - h[i - 1] === 1);
  if (seguidas) return `Hoje: de hora em hora, das ${h[0]}h às ${h[h.length - 1]}h.`;
  const horas = h.map((x) => `${x}h`);
  return `Hoje: leituras às ${horas.length === 1 ? horas[0] : `${horas.slice(0, -1).join(', ')} e ${horas[horas.length - 1]}`}.`;
}

/** Texto do toast do "Atualizar agora", a partir da resposta do POST /sicredi/extrato/sincronizar. */
export function mensagemDaSincronizacao(r: { imported?: number; duplicates?: number } | null | undefined): string {
  const novos = Number(r?.imported) || 0;
  const repetidos = Number(r?.duplicates) || 0;
  const corpo = novos === 0
    ? 'nenhum lançamento novo'
    : novos === 1 ? '1 lançamento novo' : `${novos} lançamentos novos`;
  const resto = repetidos > 0 ? ` (${repetidos} já ${repetidos === 1 ? 'existia' : 'existiam'})` : '';
  return `Extrato Sicredi atualizado: ${corpo}${resto}.`;
}
